import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import {
  useUserDocumentRoles,
  type UserManagedDocument,
} from "~/api/v2/useUserDocumentRoles";
import {
  updateDocument,
  updateUser,
  type UpdateDocumentData,
} from "~/api/v2/requests";
import { useCategories } from "~/api/swr/useCategories";
import useUsers from "~/api/swr/useUsers";
import { type User } from "~/api/types";
import { PlatformRole, UserStatus } from "~/config/types";
import { setMapItems } from "~/helpers/map";
import { printFullName } from "~/helpers/user";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { useFilter, type Filter } from "~/hooks/useFilter";
import { useUserContext } from "~/hooks/useUserContext";
import { type ActiveFilterChip } from "~/organisms/filters/ActiveFilterChips";

export type ManagerColumn = "owner" | "publisher";

export const MANAGER_COLUMNS: ManagerColumn[] = ["owner", "publisher"];

const isValidManagerForDocument = (
  document: UserManagedDocument,
  user: User | undefined,
) => {
  if (!user) return true;
  if (user.role !== PlatformRole.contributor) return true;
  return user.categories.some((c) => c.id === document.categoryId);
};

export function useOffboardUserPage() {
  const { t } = useTranslation();
  const { id: offboardedUserIdParam } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useUserContext();

  const {
    data: roles,
    isLoading: isLoadingRoles,
    mutate,
  } = useUserDocumentRoles(offboardedUserIdParam);

  const { data: users } = useUsers({
    status: [UserStatus.active, UserStatus.pending],
    roles: [
      PlatformRole.contributor,
      PlatformRole.coordinator,
      PlatformRole.superAdmin,
    ],
    putCurrentUserAtTop: true,
  });

  const { data: categories } = useCategories();

  const [overrides, setOverrides] = useState<{
    owner: Map<number, number>;
    publisher: Map<number, number>;
  }>({ owner: new Map(), publisher: new Map() });

  const [submitErrors, setSubmitErrors] = useState<Map<number, string>>(
    new Map(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const offboardedUserId = Number(offboardedUserIdParam);
  const documents = roles?.documents ?? [];

  const filterInput: Filter<UserManagedDocument>[] = [
    {
      id: "category",
      label: t(($) => $.commons.document.category),
      isMulti: true,
      options: (categories ?? []).map((category) => ({
        value: category.id.toString(),
        label: category.title,
      })),
      getValue: (document) => document.categoryId?.toString() ?? "",
    },
    {
      id: "role",
      label: t(($) => $.commons.role),
      isMulti: true,
      options: [
        { value: "owner", label: t(($) => $.commons.document.owner) },
        { value: "publisher", label: t(($) => $.commons.document.publisher) },
      ],
      getValue: (document) => {
        const documentRoles: string[] = [];
        if (document.owner?.id === offboardedUserId)
          documentRoles.push("owner");
        if (document.publisher?.id === offboardedUserId)
          documentRoles.push("publisher");
        return documentRoles;
      },
    },
  ];

  const { filters, activeFilters, handleSelectFilter, filterItems } =
    useFilter(filterInput);

  const filteredDocuments = filterItems(documents);

  const selection = useBulkSelection(
    filteredDocuments.map((document) => document.documentId),
  );

  const activeFilterChips: ActiveFilterChip[] = activeFilters.map((filter) => ({
    key: filter.id,
    label: filter.label,
    comparatorLabel:
      filter.optionIds.length === 1
        ? t(($) => $.library.filters.equals)
        : t(($) => $.library.filters.includes),
    value: filter.optionIds
      .map(
        (optionId) =>
          filter.options.find((option) => option.value === optionId)?.label ??
          optionId,
      )
      .join(", "),
    onRemove: () => handleSelectFilter(filter.id, []),
  }));

  const usersById = new Map((users ?? []).map((user) => [user.id, user]));

  const offboardedUser = usersById.get(offboardedUserId);
  const userOptions = users
    ?.filter((user) => user.id !== offboardedUserId)
    .map((user) => ({
      value: user.id.toString(),
      label: printFullName(user, user.id === currentUser.id),
    }));

  const getManagerId = (document: UserManagedDocument, column: ManagerColumn) =>
    overrides[column].get(document.documentId) ?? document[column]?.id ?? null;

  const setManager = (
    column: ManagerColumn,
    documentIds: Set<number>,
    value: number,
  ) => {
    const targetIds = documents
      .filter(
        (document) =>
          documentIds.has(document.documentId) &&
          document[column]?.id === offboardedUserId,
      )
      .map((document) => document.documentId);
    setOverrides((prev) => ({
      ...prev,
      [column]: setMapItems(prev[column], targetIds, value),
    }));
    setSubmitErrors((prev) => {
      const next = new Map(prev);
      for (const documentId of targetIds) next.delete(documentId);
      return next;
    });
  };

  const managerValidationError = (
    document: UserManagedDocument,
    column: ManagerColumn,
  ) => {
    const managerId = getManagerId(document, column);
    if (managerId === null || managerId === offboardedUserId) {
      return undefined;
    }
    if (isValidManagerForDocument(document, usersById.get(managerId))) {
      return undefined;
    }
    return column === "owner"
      ? t(($) => $.validations.contributorOwnerMustBeInCategory)
      : t(($) => $.validations.contributorPublisherMustBeInCategory);
  };

  const getCellError = (document: UserManagedDocument, column: ManagerColumn) =>
    managerValidationError(document, column) ??
    submitErrors.get(document.documentId);

  const canDisableUser = documents.every((document) =>
    MANAGER_COLUMNS.every(
      (column) =>
        getManagerId(document, column) !== offboardedUserId &&
        managerValidationError(document, column) === undefined,
    ),
  );

  const onDisableUser = async () => {
    setIsSubmitting(true);
    try {
      const changes = documents
        .map((document) => {
          const data: UpdateDocumentData = {};

          const ownerId = overrides.owner.get(document.documentId);
          if (ownerId !== undefined) {
            data.ownerId = ownerId;
          }

          const publisherId = overrides.publisher.get(document.documentId);
          if (publisherId !== undefined) {
            data.publisherId = publisherId;
          }
          return { documentId: document.documentId, data };
        })
        .filter((change) => Object.keys(change.data).length > 0);

      const errors = new Map<number, string>();
      await Promise.all(
        changes.map(async ({ documentId, data }) => {
          try {
            await updateDocument(documentId, data);
          } catch (error) {
            errors.set(
              documentId,
              error instanceof Error
                ? error.message
                : t(($) => $.toasts.error.generic),
            );
          }
        }),
      );

      if (errors.size > 0) {
        setSubmitErrors(errors);
        void mutate();
        toast.error(t(($) => $.toasts.error.generic));
        return;
      }

      await updateUser(offboardedUserId, { status: UserStatus.deactivated });

      toast.success(t(($) => $.settings.userManagement.offboardSuccess));
      void navigate("/users");
    } catch {
      void mutate();
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    offboardedUserIdParam,
    isLoadingRoles,
    hasData: roles !== undefined,
    offboardedUserId,
    offboardedUser,
    userOptions,
    filters,
    activeFilterChips,
    handleSelectFilter,
    filteredDocuments,
    selection,
    isSubmitting,
    canDisableUser,
    onDisableUser,
    getManagerId,
    getCellError,
    setManager,
  };
}
